import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import {
    AppBar,
    Box,
    Button,
    Container,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

export default function Navbar() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const navItems = [
        { name: 'Ana Sayfa', path: '/' },
        { name: 'Özellikler', path: '#features' },
        { name: 'Fiyatlandırma', path: '#pricing' },
        { name: 'Karşılaştırma', path: '#comparison' }
    ];

    const demoButton = { name: 'Demo Talep Et', path: '#demo-form', isDemoButton: true };

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 50;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrolled]);

    // Tally script yükleme
    useEffect(() => {
        if (!document.querySelector('script[src="https://tally.so/widgets/embed.js"]')) {
            const script = document.createElement('script');
            script.src = 'https://tally.so/widgets/embed.js';
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    const scrollToSection = (sectionId) => {
        setDrawerOpen(false);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const drawer = (
        <Box
            sx={{
                width: isSmallMobile ? '100vw' : 300,
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'white',
                position: 'fixed',
                top: 0,
                right: 0,
                zIndex: 2000,
                boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                overflowY: 'auto',
            }}
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                    <Typography
                        variant="h6"
                        component={RouterLink}
                        to="/"
                        onClick={toggleDrawer(false)}
                        sx={{ fontWeight: 700, color: theme.palette.text.primary, textDecoration: 'none', fontSize: '1.1rem' }}
                    >
                        Helpigo
                    </Typography>
                    <Typography component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 300 }} aria-hidden>
                        /
                    </Typography>
                    <Box
                        component="a"
                        href="https://helpigo.app/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Helpigo App — helpigo.app"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ display: 'flex', alignItems: 'center', lineHeight: 0 }}
                    >
                        <Box
                            component="img"
                            src={`${process.env.PUBLIC_URL}/helpigo-logo-design-handoff.svg`}
                            alt="Helpigo App"
                            sx={{ height: 22, width: 'auto', maxWidth: 'min(160px, 45vw)' }}
                        />
                    </Box>
                </Box>
                <IconButton onClick={toggleDrawer(false)}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <List sx={{ py: 2 }}>
                    {navItems.map((item, index) => (
                        <ListItem
                            button
                            key={index}
                            onClick={() => {
                                if (item.path.startsWith('#')) {
                                    scrollToSection(item.path.substring(1));
                                } else {
                                    window.location.href = item.path;
                                }
                            }}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    bgcolor: 'rgba(37, 99, 235, 0.06)',
                                }
                            }}
                        >
                            <ListItemText
                                primary={item.name}
                                primaryTypographyProps={{
                                    fontWeight: 500,
                                    color: theme.palette.text.primary,
                                }}
                            />
                        </ListItem>
                    ))}

                    {/* Demo Talep Et Butonu - Mobil */}
                    <Box sx={{ px: 2, py: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            data-tally-open="wMV9kA"
                            data-tally-layout="modal"
                            data-tally-width="400"
                            data-tally-auto-close="0"
                            sx={{
                                py: 1.5,
                                backgroundColor: theme.palette.accent?.main || '#635BFF',
                                color: 'white',
                                borderRadius: '8px',
                                fontWeight: 600,
                                '&:hover': {
                                    bgcolor: theme.palette.accent?.dark || '#4F46E5',
                                }
                            }}
                        >
                            {demoButton.name}
                        </Button>
                    </Box>
                </List>
            </Box>
            <Box sx={{ p: 2, borderTop: '1px solid #eee', flexShrink: 0 }}>
                <Button
                    variant="text"
                    fullWidth
                    component={RouterLink}
                    to="/login"
                    sx={{
                        mb: 1.5,
                        borderRadius: '8px',
                        py: 1.5,
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                    }}
                >
                    Giriş Yap
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    component={RouterLink}
                    to="/register"
                    sx={{
                        borderRadius: '8px',
                        py: 1.5,
                        fontWeight: 600,
                        borderColor: theme.palette.text.primary,
                        borderWidth: '1.5px',
                        color: theme.palette.text.primary,
                    }}
                >
                    Kayıt Ol
                </Button>
            </Box>
        </Box>
    );

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    boxShadow: scrolled ? '0 1px 3px rgba(0, 0, 0, 0.05)' : 'none',
                    bgcolor: 'white',
                    transition: 'all 0.2s ease',
                    height: scrolled ? (isMobile ? '60px' : '70px') : (isMobile ? '70px' : '80px'),
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 1100,
                    borderBottom: scrolled ? `1px solid ${theme.palette.grey[100]}` : 'none',
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ height: '100%' }}>
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 0.75, sm: 1.25 },
                                    mr: { xs: 1, md: 2 },
                                    flexShrink: 0,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    component={RouterLink}
                                    to="/"
                                    sx={{
                                        fontWeight: 700,
                                        textDecoration: 'none',
                                        fontSize: { xs: '1.15rem', md: '1.5rem' },
                                        color: theme.palette.text.primary,
                                        lineHeight: 1,
                                        '&:hover': { opacity: 0.85 },
                                    }}
                                >
                                    Helpigo
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        fontWeight: 300,
                                        fontSize: { xs: '1.1rem', md: '1.35rem' },
                                        lineHeight: 1,
                                        userSelect: 'none',
                                    }}
                                    aria-hidden
                                >
                                    /
                                </Typography>
                                <Box
                                    component="a"
                                    href="https://helpigo.app/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Helpigo App — helpigo.app sitesini yeni sekmede aç"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        lineHeight: 0,
                                        '&:hover': { opacity: 0.88 },
                                        '&:focus-visible': {
                                            outline: `2px solid ${theme.palette.accent?.main || '#2563eb'}`,
                                            outlineOffset: 4,
                                            borderRadius: 1,
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}/helpigo-logo-design-handoff.svg`}
                                        alt="Helpigo App"
                                        sx={{
                                            height: { xs: 20, sm: 24, md: 28 },
                                            width: 'auto',
                                            display: 'block',
                                        }}
                                    />
                                </Box>
                            </Box>
                        </motion.div>

                        <Box sx={{ flexGrow: 1 }} />

                        {!isMobile ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {navItems.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                    >
                                        <Button
                                            onClick={() => {
                                                if (item.path.startsWith('#')) {
                                                    scrollToSection(item.path.substring(1));
                                                }
                                            }}
                                            component={item.path.startsWith('#') ? 'button' : RouterLink}
                                            to={item.path.startsWith('#') ? undefined : item.path}
                                            sx={{
                                                mx: { sm: 0.5, md: 1 },
                                                color: theme.palette.text.primary,
                                                fontWeight: 500,
                                                '&:hover': {
                                                    color: theme.palette.text.primary,
                                                    backgroundColor: theme.palette.grey[50],
                                                },
                                                fontSize: { sm: '0.875rem', md: '0.9375rem' }
                                            }}
                                        >
                                            {item.name}
                                        </Button>
                                    </motion.div>
                                ))}

                                {/* Demo Talep Et Butonu */}
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: navItems.length * 0.1 }}
                                >
                                    <Button
                                        data-tally-open="wMV9kA"
                                        data-tally-layout="modal"
                                        data-tally-width="400"
                                        data-tally-auto-close="0"
                                        sx={{
                                            mx: { sm: 0.5, md: 1 },
                                            color: 'white',
                                            fontWeight: 600,
                                            backgroundColor: theme.palette.accent?.main || '#635BFF',
                                            borderRadius: '8px',
                                            px: 2.5,
                                            py: 1,
                                            '&:hover': {
                                                backgroundColor: theme.palette.accent?.dark || '#4F46E5',
                                            },
                                            fontSize: { sm: '0.875rem', md: '0.9375rem' },
                                            boxShadow: '0 1px 3px rgba(37, 99, 235, 0.25)',
                                        }}
                                    >
                                        {demoButton.name}
                                    </Button>
                                </motion.div>

                                <Box sx={{ display: 'flex', ml: 2 }}>
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, delay: navItems.length * 0.1 }}
                                    >
                                        <Button
                                            variant="text"
                                            component={RouterLink}
                                            to="/login"
                                            sx={{
                                                mr: 2,
                                                color: theme.palette.text.primary,
                                                '&:hover': {
                                                    color: theme.palette.text.primary,
                                                    backgroundColor: theme.palette.grey[50],
                                                },
                                                fontSize: { sm: '0.875rem', md: '0.9375rem' }
                                            }}
                                        >
                                            Giriş Yap
                                        </Button>
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, delay: (navItems.length + 1) * 0.1 }}
                                    >
                                        <Button
                                            variant="outlined"
                                            component={RouterLink}
                                            to="/register"
                                            sx={{
                                                borderRadius: '8px',
                                                borderColor: theme.palette.text.primary,
                                                borderWidth: '1.5px',
                                                color: theme.palette.text.primary,
                                                '&:hover': {
                                                    borderColor: theme.palette.text.primary,
                                                    backgroundColor: theme.palette.grey[50],
                                                },
                                                fontSize: { sm: '0.875rem', md: '0.9375rem' }
                                            }}
                                        >
                                            Kayıt Ol
                                        </Button>
                                    </motion.div>
                                </Box>
                            </Box>
                        ) : (
                            <IconButton
                                size="large"
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={toggleDrawer(true)}
                                sx={{
                                    color: theme.palette.text.primary,
                                    padding: '8px',
                                    '&:hover': {
                                        backgroundColor: theme.palette.grey[50]
                                    }
                                }}
                            >
                                <MenuIcon fontSize="large" />
                            </IconButton>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                sx={{
                    zIndex: 1500,
                    '& .MuiDrawer-paper': {
                        width: isSmallMobile ? '100%' : 300,
                        boxSizing: 'border-box',
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
} 